import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, CheckSquare, GitBranch, Bot, Search, LogOut, Fuel,
  Users, FileText, ReceiptText, Banknote, Truck, Warehouse, Boxes,
  ArrowLeftRight, ShoppingCart, HandCoins, ClipboardList, PackageCheck,
  Scale, AlertTriangle, ShieldCheck, Settings, UserCog, Layers, ChevronRight,
  Radar, Bell, CalendarClock, TrendingUp, Database, ShieldAlert, BookOpen,
} from "lucide-react";

const NAV = [
  { section: null, items: [
    { to: "/", label: "Executive Control Room", icon: LayoutDashboard, key: "dashboard" },
    { to: "/approvals", label: "Approval Center", icon: CheckSquare, key: "approvals" },
    { to: "/transaction-360", label: "Transaction 360°", icon: GitBranch, key: "t360" },
    { to: "/ai", label: "PetroFlow AI Assistant", icon: Bot, key: "ai" },
    { to: "/help", label: "Help & User Guide", icon: BookOpen, key: "help" },
  ]},
  { section: "Procurement", items: [
    { to: "/suppliers", label: "Suppliers", icon: Users, key: "suppliers", roles: ["procurement", "finance", "executive", "compliance", "auditor"] },
    { to: "/spas", label: "Sales & Purchase Agreements", icon: FileText, key: "spas", roles: ["procurement", "finance", "executive", "compliance", "auditor"] },
    { to: "/supplier-invoices", label: "Supplier Invoices", icon: ReceiptText, key: "sinv", roles: ["procurement", "finance", "executive", "auditor"] },
  ]},
  { section: "Finance", items: [
    { to: "/supplier-payments", label: "Supplier Payments", icon: Banknote, key: "spay", roles: ["finance", "executive", "auditor"] },
    { to: "/customer-payments", label: "Customer Payments", icon: HandCoins, key: "cpay", roles: ["finance", "sales", "executive", "auditor"] },
    { to: "/landed-cost", label: "Landed Cost & Margin", icon: Layers, key: "landed", roles: ["finance", "procurement", "executive", "auditor"] },
  ]},
  { section: "Petroleum Ops", items: [
    { to: "/lprc-transfers", label: "LPRC Transfers", icon: ArrowLeftRight, key: "lprc", roles: ["operations", "executive", "auditor"] },
    { to: "/tanks", label: "Tanks & Inventory", icon: Warehouse, key: "tanks", roles: ["operations", "inventory", "sales", "executive", "auditor"] },
    { to: "/inventory-movements", label: "Inventory Movements", icon: Boxes, key: "mov", roles: ["operations", "inventory", "executive", "auditor"] },
    { to: "/inventory-adjustments", label: "Inventory Adjustments", icon: Scale, key: "adj", roles: ["operations", "inventory", "executive", "auditor"] },
  ]},
  { section: "Sales", items: [
    { to: "/customers", label: "Customers", icon: Users, key: "customers", roles: ["sales", "finance", "executive", "auditor"] },
    { to: "/sales-orders", label: "Sales Orders", icon: ShoppingCart, key: "so", roles: ["sales", "finance", "executive", "auditor"] },
  ]},
  { section: "Logistics", items: [
    { to: "/delivery-orders", label: "Delivery Orders", icon: ClipboardList, key: "do", roles: ["logistics", "sales", "executive", "auditor"] },
    { to: "/pod", label: "Dispatch & POD", icon: PackageCheck, key: "pod", roles: ["logistics", "sales", "operations", "executive", "auditor"] },
    { to: "/deliveries", label: "Deliveries", icon: PackageCheck, key: "del", roles: ["logistics", "sales", "executive", "auditor"] },
    { to: "/vehicles", label: "Fleet & Tankers", icon: Truck, key: "veh", roles: ["logistics", "operations", "executive", "auditor"] },
    { to: "/drivers", label: "Drivers", icon: Users, key: "drv", roles: ["logistics", "operations", "executive", "auditor"] },
    { to: "/routes", label: "Routes", icon: GitBranch, key: "rte", roles: ["logistics", "operations", "executive", "auditor"] },
  ]},
  { section: "Controls & Compliance", items: [
    { to: "/risk", label: "AI Risk Radar", icon: Radar, key: "risk", roles: ["finance", "operations", "compliance", "executive", "auditor"] },
    { to: "/forecast", label: "AI Forecasting", icon: TrendingUp, key: "forecast", roles: ["finance", "operations", "executive", "auditor"] },
    { to: "/reconciliation-engine", label: "Reconciliation Engine", icon: Scale, key: "recon-engine", roles: ["finance", "operations", "compliance", "executive", "auditor"] },
    { to: "/reconciliations", label: "Reconciliation Records", icon: Scale, key: "recon", roles: ["finance", "operations", "compliance", "executive", "auditor"] },
    { to: "/exceptions", label: "Exceptions", icon: AlertTriangle, key: "exc", roles: ["finance", "operations", "compliance", "executive", "auditor"] },
    { to: "/hsse", label: "HSSE", icon: ShieldAlert, key: "hsse", roles: ["compliance", "operations", "logistics", "executive", "auditor"] },
    { to: "/notifications", label: "Notifications", icon: Bell, key: "notif" },
    { to: "/audit", label: "Audit Trail", icon: ShieldCheck, key: "audit", roles: ["auditor", "compliance", "executive"] },
  ]},
  { section: "Administration", items: [
    { to: "/products", label: "Product Master", icon: Layers, key: "prod", roles: ["operations", "inventory", "procurement", "auditor"] },
    { to: "/data-import", label: "Data Migration", icon: Database, key: "import", roles: ["executive"] },
    { to: "/config", label: "Configuration & FX", icon: Settings, key: "config", roles: ["finance", "executive"] },
    { to: "/admin/periods", label: "Fiscal Periods", icon: CalendarClock, key: "periods", roles: ["finance", "executive"] },
    { to: "/admin/users", label: "Users & Roles", icon: UserCog, key: "users", roles: [] },
  ]},
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const boxRef = useRef();

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setRes([]); return; }
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); setOpen(true); }
      catch (e) { console.error("Search failed:", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const routeFor = { suppliers: "/suppliers", spas: "/spas", supplier_invoices: "/supplier-invoices",
    customers: "/customers", sales_orders: "/sales-orders", delivery_orders: "/delivery-orders",
    lprc_transfers: "/lprc-transfers", tanks: "/tanks" };

  return (
    <div className="relative w-full max-w-md" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        data-testid="input-global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res.length && setOpen(true)}
        placeholder="Search suppliers, SPAs, orders, tanks…"
        className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
      />
      {open && res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-auto">
          {res.map((r) => (
            <button
              key={r.id}
              data-testid={`search-result-${r.id}`}
              onClick={() => { setOpen(false); setQ(""); nav(routeFor[r.collection] || "/"); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-800">{r.title}</span>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">{r.type}</span>
              </div>
              <div className="text-xs text-slate-500">{r.subtitle} · {r.status}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-100">
      <aside className="w-[260px] shrink-0 bg-[#12346B] text-slate-200 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <img src="/prime-logo.jpg" alt="Prime Contractors, Inc." className="h-10 w-10 rounded-full object-cover bg-white ring-1 ring-white/20" />
          <div className="leading-tight">
            <div className="font-head font-extrabold text-white text-sm">PCI PetroFlow</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Petroleum Trading ERP</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV.map((grp, gi) => {
            const visible = grp.items.filter((it) => {
              if (!it.roles) return true;
              if (user?.role === "admin") return true;
              return it.roles.includes(user?.role);
            });
            if (visible.length === 0) return null;
            return (
            <div key={grp.section || `grp-${gi}`} className="mb-2">
              {grp.section && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{grp.section}</div>
              )}
              {visible.map((it) => (
                <NavLink
                  key={it.key}
                  to={it.to}
                  end={it.to === "/"}
                  data-testid={`sidebar-nav-${it.key}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                      isActive ? "bg-white/10 text-white font-semibold" : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{it.label}</span>
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 ml-[260px] flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center gap-4 px-6 sticky top-0 z-40">
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right leading-tight">
              <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{user?.role}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#12346B] text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <button
              data-testid="btn-logout"
              onClick={async () => { await logout(); nav("/login"); }}
              className="h-9 w-9 rounded-md border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 relative">
          <div aria-hidden className="pointer-events-none fixed right-8 bottom-16 w-[520px] h-[520px] opacity-[0.035] bg-center bg-no-repeat bg-contain z-0"
               style={{ backgroundImage: "url('/prime-logo.jpg')" }} />
          <div className="relative z-10">{children}</div>
        </main>
        <footer className="px-6 py-3 text-[11px] text-slate-400 border-t border-slate-200 bg-white flex items-center gap-1">
          PCI PetroFlow ERP™ · Prime Contractors, Inc. · Technology Partner: Smithsonian Group Inc. <ChevronRight className="h-3 w-3" /> Synthetic demo data
        </footer>
      </div>
    </div>
  );
}
